import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  Button,
  FormGroup,
  Heading,
  LoaderButton,
  Radio,
  RadioGroup,
  Select,
  Text,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { ConfirmModal, TextEditor } from 'slovlex-idsk-react-components';
import { DolozkaZlucitelnostiFormData }
  from '../../../../model/sprievodnyDokument/dolozkaZlucitelnosti/DolozkaZlucitelnostiFormData';
import { useClient } from '../../../../context/ClientStatusContext';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import routes from '../../../../routing/routes';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import { ClientResponse } from '../../../../model/ClientResponse';
import Uuid from '../../../../model/Uuid';
import { ZlucitelnostSPravomEuEnum }
  from '../../../../model/sprievodnyDokument/dolozkaZlucitelnosti/ZlucitelnostSPravomEuEnum';
import './dolozkaZlucitelnosti.scss';
import { pouzivatelIsAdmin } from '../../../../function/permissions';
import { useUserContext } from '../../../../context/UserContext';

export interface PointProps {
  formData: DolozkaZlucitelnostiFormData;
  setFormData: Dispatch<SetStateAction<DolozkaZlucitelnostiFormData>>;
}

export interface PointPropsWithInitData extends PointProps {
  initFormData: DolozkaZlucitelnostiFormData;
}

export const Point3 = ({ formData, setFormData, initFormData }: PointPropsWithInitData): ReactElement => (
  <section className="dolozka-zlucitelnosti-section">
    <Heading size="m">3. Predmet návrhu právneho predpisu</Heading>
    <RadioGroup inline small>
      <Radio
        title="Nie je upravený v práve Európskej únie"
        checked={formData.upravenyVPraveEu === false}
        id="upravenyVPraveEuNie"
        value="false"
        name="upravenyVPraveEuNie"
        onChange={() => {
          setFormData({ ...formData, upravenyVPraveEu: false });
        }}
      />
      <Radio
        title="Je upravený v práve Európskej únie"
        checked={formData.upravenyVPraveEu === true}
        id="upravenyVPraveEuAno"
        value="true"
        name="upravenyVPraveEuAno"
        onChange={() => {
          setFormData({ ...formData, upravenyVPraveEu: true });
        }}
      />
    </RadioGroup>
    {formData.upravenyVPraveEu && (
      <>
        <FormGroup>
          <Text className="dolozka-zlucitelnosti-label">
            a) v primárnom práve:
          </Text>
          <TextEditor
            initialState={initFormData.primarnePravo}
            onChange={(data: string) => setFormData({ ...formData, primarnePravo: data })}
            maxLength={2048}
            showRemaining
          />
        </FormGroup>
        <FormGroup>
          <Text className="dolozka-zlucitelnosti-label">
            b) v sekundárnom práve:
          </Text>
          <TextEditor
            initialState={initFormData.sekundarnePravo}
            onChange={(data: string) => setFormData({ ...formData, sekundarnePravo: data })}
            maxLength={2048}
            showRemaining
          />
        </FormGroup>
        <FormGroup>
          <Text className="dolozka-zlucitelnosti-label">
            c) v judikatúre Súdneho dvora Európskej únie:
          </Text>
          <TextEditor
            initialState={initFormData.judikaturaSudnehoDvoraEu}
            onChange={(data: string) => setFormData({ ...formData, judikaturaSudnehoDvoraEu: data })}
            maxLength={2048}
            showRemaining
          />
        </FormGroup>
      </>
    )}
  </section>
);

export const Point4 = ({ formData, setFormData, initFormData }: PointPropsWithInitData)
  : ReactElement => formData.upravenyVPraveEu && (
  <section className="dolozka-zlucitelnosti-section">
    <Heading size="m">4. Záväzky Slovenskej republiky vo vzťahu k Európskej únii</Heading>
    <FormGroup>
      <Text className="dolozka-zlucitelnosti-label">
        a) lehota na prebranie príslušného právneho aktu Európskej únie,
        príp. aj osobitná lehota účinnosti jeho ustanovení
      </Text>
      <TextEditor
        initialState={initFormData.lehotaPravnyAktEu}
        onChange={(data: string) => setFormData({ ...formData, lehotaPravnyAktEu: data })}
        maxLength={2048}
        showRemaining
      />
    </FormGroup>
    <FormGroup>
      <Text className="dolozka-zlucitelnosti-label">
        b) informácia o začatí konania v rámci „EÚ Pilot“ alebo o začatí postupu Európskej komisie,
        alebo o konaní Súdneho dvora Európskej únie proti Slovenskej republike podľa čl. 258 a 260 Zmluvy
        o fungovaní Európskej únie v jej platnom znení, spolu s uvedením konkrétnych vytýkaných nedostatkov
        a požiadaviek na zabezpečenie nápravy so zreteľom na nariadenie Európskeho parlamentu a Rady (ES)
        č. 1049/2001 z 30. mája 2001 o prístupe verejnosti k dokumentom Európskeho parlamentu, Rady a Komisie
      </Text>
      <TextEditor
        initialState={initFormData.informaciaZacatieKonania}
        onChange={(data: string) => setFormData({ ...formData, informaciaZacatieKonania: data })}
        maxLength={2048}
        showRemaining
      />
    </FormGroup>
    <FormGroup>
      <Text className="dolozka-zlucitelnosti-label">
        c) informácia o právnych predpisoch, v ktorých sú uvádzané právne akty Európskej únie
        už prebrané, spolu s uvedením rozsahu ich prebrania, príp. potreby prijatia ďalších úprav
      </Text>
      <TextEditor
        initialState={initFormData.informaciaPravnePredpisy}
        onChange={(data: string) => setFormData({ ...formData, informaciaPravnePredpisy: data })}
        maxLength={2048}
        showRemaining
      />
    </FormGroup>
  </section>
);

export const Point5 = ({ formData, setFormData, initFormData }: PointPropsWithInitData)
  : ReactElement => formData.upravenyVPraveEu && (
  <section className="dolozka-zlucitelnosti-section">
    <Heading size="m">5. Návrh právneho predpisu je zlučiteľný s právom Európskej únie</Heading>
    <FormGroup>
      <Select
        id="stadium"
        name="stadium"
        width="full"
        onChange={({ target }) => setFormData({ ...formData, zlucitelnostSPravomEu: target.value })}
        value={formData.zlucitelnostSPravomEu || ZlucitelnostSPravomEuEnum.CIASTOCNE}
      >
        <option value={ZlucitelnostSPravomEuEnum.UPLNE}>úplne</option>
        <option value={ZlucitelnostSPravomEuEnum.CIASTOCNE}>čiastočne</option>
        <option value={ZlucitelnostSPravomEuEnum.NIE_JE}>nie je</option>
      </Select>
    </FormGroup>
    {formData.zlucitelnostSPravomEu !== ZlucitelnostSPravomEuEnum.UPLNE && (
      <FormGroup>
        <Text className="dolozka-zlucitelnosti-label">
          Dôvody, predpokladaný termín a spôsob dosiahnutia úplného súladu.
        </Text>
        <TextEditor
          initialState={initFormData.dovody}
          onChange={(data: string) => setFormData({ ...formData, dovody: data })}
          maxLength={2048}
          showRemaining
        />
      </FormGroup>
    )}
  </section>
);

interface DolozkaZlucitelnostiProps {
  onSubmit: (uuid: Uuid, form: DolozkaZlucitelnostiFormData) => Promise<ClientResponse<void>>;
  material: LegislativnyMaterialDetail,
  initFormData: DolozkaZlucitelnostiFormData,
}

const DolozkaZlucitelnosti = ({
  onSubmit, material, initFormData,
}: DolozkaZlucitelnostiProps): ReactElement => {
  const [formData, setFormData] = useState<DolozkaZlucitelnostiFormData>(initFormData);
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    type,
    year,
    index,
    stadiumUuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, stadiumUuid: string }>();
  const { pouzivatel } = useUserContext();

  const {
    wrapClient: submitDolozkaZlucitelnosti,
    status,
  } = useClient(onSubmit);

  const onSubmitForm = async (): Promise<void> => {
    await submitDolozkaZlucitelnosti(material.uuid, formData, stadiumUuid);
    navigate(pouzivatelIsAdmin(pouzivatel)
      ? routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(type, year, index, stadiumUuid)
      : routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index));
    displaySuccess('Dokument bol úspešne uložený');
  };
  return (
    <div className="dolozka-zlucitelnosti">
      <section className="dolozka-zlucitelnosti-section">
        <Heading size="m">1. Navrhovateľ právneho predpisu</Heading>
        <Text type="span">{material.zodpovednaInstitucia?.nazov}</Text>
      </section>
      <section className="dolozka-zlucitelnosti-section">
        <Heading size="m">2. Názov návrhu právneho predpisu</Heading>
        <Text type="span">{material.nazov}</Text>
      </section>
      <Point3 formData={formData} setFormData={setFormData} initFormData={initFormData} />
      <Point4 formData={formData} setFormData={setFormData} initFormData={initFormData} />
      <Point5 formData={formData} setFormData={setFormData} initFormData={initFormData} />
      <FormGroup className="dolozka-zlucitelnosti__form-buttons">
        <ConfirmModal
          question="Naozaj si prajete uložiť formulár a vytvoriť sprievodný dokument?"
          description={'Ak upravujete existujúci formulár, budú údaje tohto formulára prepísané '
            + 'a výsledný dokument, ktorý vznikol na základe tohto formulára, bude nahradený.'}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              loading={status.loading}
              type="submit"
              size="large"
              disableOutsideClickWhileLoading
            >Uložiť
            </LoaderButton>
          )}
          doAfterConfirm={() => onSubmitForm()}
        />
        <Button
          className="dolozka-zlucitelnosti__buttons"
          type="button"
          buttonStyle="third"
          size="large"
          disabled={status.loading}
          onClick={() => navigate(-1)}
        >Späť
        </Button>
      </FormGroup>
    </div>
  );
};

export default DolozkaZlucitelnosti;
